﻿import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getDashboardTexts, interpolate } from "../../translate/pageTexts";

const chartPalette = [
  "#0f766e",
  "#14b8a6",
  "#22c55e",
  "#f59e0b",
  "#f97316",
  "#3b82f6",
  "#8b5cf6",
  "#ef4444"
];

const getPeriodOptions = texts => [
  { value: "today", label: texts.periods.today },
  { value: "week", label: texts.periods.week },
  { value: "month", label: texts.periods.month },
  { value: "quarter", label: texts.periods.quarter },
  { value: "year", label: texts.periods.year },
  { value: "custom", label: texts.periods.custom }
];

const toDateInputValue = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

const getDateBasisOptions = texts => [
  { value: "closedAt", label: texts.dateBasis.closedAt },
  { value: "createdAt", label: texts.dateBasis.createdAt }
];

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";

  return ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  heroCard: {
    padding: theme.spacing(3),
    borderRadius: 28,
    background: isDark
      ? "linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(17, 24, 39, 0.94) 52%, rgba(6, 78, 59, 0.16) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,248,246,0.98) 52%, rgba(232,244,241,0.98) 100%)",
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.12)",
    boxShadow: isDark ? "0 22px 56px rgba(2, 6, 23, 0.38)" : "0 22px 56px rgba(18, 140, 126, 0.12)",
    marginBottom: theme.spacing(3)
  },
  heroHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  heroIdentity: {
    minWidth: 0
  },
  heroNoticeSlot: {
    flex: 1,
    minWidth: 260,
    maxWidth: 420,
    [theme.breakpoints.down("md")]: {
      maxWidth: "100%"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minWidth: 0
    }
  },
  heroEyebrow: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    letterSpacing: "0.08em",
    fontSize: "0.75rem",
    textTransform: "uppercase"
  },
  heroTitle: {
    marginTop: theme.spacing(1),
    fontWeight: 800,
    lineHeight: 1.08
  },
  heroActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  heroActionsPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start"
    }
  },
  heroActionsLabel: {
    display: "none"
  },
  commercialAlert: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 20,
    border: isDark ? "1px solid rgba(245, 158, 11, 0.32)" : "1px solid rgba(245, 158, 11, 0.24)",
    background: isDark
      ? "linear-gradient(135deg, rgba(120, 53, 15, 0.44) 0%, rgba(146, 64, 14, 0.28) 100%)"
      : "linear-gradient(135deg, rgba(255, 251, 235, 0.98) 0%, rgba(254, 243, 199, 0.98) 100%)",
    boxShadow: isDark ? "0 10px 24px rgba(15, 23, 42, 0.30)" : "0 10px 24px rgba(245, 158, 11, 0.10)"
  },
  commercialAlertDanger: {
    border: isDark ? "1px solid rgba(248, 113, 113, 0.34)" : "1px solid rgba(239, 68, 68, 0.22)",
    background: isDark
      ? "linear-gradient(135deg, rgba(127, 29, 29, 0.44) 0%, rgba(153, 27, 27, 0.28) 100%)"
      : "linear-gradient(135deg, rgba(254, 242, 242, 0.98) 0%, rgba(254, 226, 226, 0.98) 100%)"
  },
  commercialAlertTitle: {
    fontWeight: 800,
    color: isDark ? "#fde68a" : "#92400e"
  },
  commercialAlertTitleDanger: {
    color: isDark ? "#fecaca" : "#991b1b"
  },
  commercialAlertText: {
    marginTop: theme.spacing(0.5),
    color: isDark ? "#fcd34d" : "#7c2d12",
    lineHeight: 1.5
  },
  commercialAlertTextDanger: {
    color: isDark ? "#fda4af" : "#7f1d1d"
  },
  actionButton: {
    borderRadius: 999,
    fontWeight: 700,
    paddingInline: theme.spacing(1.75),
    justifyContent: "center",
    minHeight: 42,
    whiteSpace: "nowrap"
  },
  periodFilters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2.5)
  },
  periodCoverage: {
    marginTop: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  periodButton: {
    borderRadius: 999,
    fontWeight: 700,
    minWidth: 0,
    paddingInline: theme.spacing(2)
  },
  spotlightGrid: {
    marginTop: theme.spacing(2.5)
  },
  spotlightCard: {
    padding: theme.spacing(2),
    borderRadius: 22,
    minHeight: 148,
    background: isDark
      ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.92) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(245,250,248,0.98) 100%)",
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.10)",
    boxShadow: isDark ? "0 14px 34px rgba(2, 6, 23, 0.30)" : "0 14px 34px rgba(15, 23, 42, 0.06)"
  },
  spotlightHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    color: isDark ? "#5eead4" : "#0b6b60"
  },
  spotlightTitle: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    fontSize: "0.82rem",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  spotlightValue: {
    marginTop: theme.spacing(1.25),
    fontWeight: 800,
    color: theme.palette.text.primary,
    lineHeight: 1.2
  },
  spotlightSubtitle: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  panel: {
    height: "100%",
    padding: theme.spacing(2.5),
    borderRadius: 24,
    border: isDark ? "1px solid rgba(45, 212, 191, 0.16)" : "1px solid rgba(18, 140, 126, 0.10)",
    boxShadow: isDark ? "0 18px 40px rgba(2, 6, 23, 0.32)" : "0 18px 40px rgba(15, 23, 42, 0.08)",
    background: isDark
      ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(17, 24, 39, 0.94) 100%)"
      : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)"
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column"
    }
  },
  panelTitle: {
    color: isDark ? "#5eead4" : "#0b6b60",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.82rem"
  },
  panelSubtitle: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    lineHeight: 1.5
  },
  lineFilters: {
    minWidth: 190,
    "& .MuiOutlinedInput-root": {
      borderRadius: 14
    }
  },
  performanceFilters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      justifyContent: "flex-start"
    }
  },
  performanceStatsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2)
  },
  emptyState: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(4)
  },
  topContactsList: {
    display: "grid",
    gap: theme.spacing(1)
  },
  topContactsItem: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    gap: theme.spacing(1.25),
    alignItems: "center",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 16,
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.08)" : "rgba(18, 140, 126, 0.05)"
  },
  rankBadge: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.18)" : "rgba(18, 140, 126, 0.12)",
    color: isDark ? "#99f6e4" : "#0b6b60",
    fontWeight: 800,
    fontSize: "0.8rem"
  },
  topContactsName: {
    fontWeight: 700,
    color: theme.palette.text.primary
  },
  topContactsMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.88rem",
    marginTop: theme.spacing(0.25)
  },
  topContactsValue: {
    fontWeight: 800,
    color: theme.palette.text.primary
  },
  statList: {
    display: "grid",
    gap: theme.spacing(1.25)
  },
  statItem: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 16,
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.09)" : "rgba(18, 140, 126, 0.06)"
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.84rem"
  },
  statValue: {
    marginTop: theme.spacing(0.35),
    fontWeight: 800,
    color: theme.palette.text.primary
  },
  ratingHeaderStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2)
  },
  ratingScore: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(0.75)
  },
  ratingScoreValue: {
    fontSize: "1.7rem",
    fontWeight: 900,
    color: isDark ? "#fde68a" : "#92400e"
  },
  ratingDistribution: {
    display: "grid",
    gap: theme.spacing(1)
  },
  ratingDistributionRow: {
    display: "grid",
    gridTemplateColumns: "64px 1fr 42px",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  ratingBarTrack: {
    height: 10,
    borderRadius: 999,
    overflow: "hidden",
    backgroundColor: isDark ? "rgba(148, 163, 184, 0.20)" : "rgba(15, 23, 42, 0.08)"
  },
  ratingBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #0f766e 0%, #f59e0b 100%)"
  },
  ratingLatestList: {
    display: "grid",
    gap: theme.spacing(1)
  },
  ratingLatestItem: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 16,
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.08)" : "rgba(18, 140, 126, 0.05)"
  },
  ratingLatestMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.84rem",
    marginTop: theme.spacing(0.5)
  },

  /* ===== KPI (lead-card style) ===== */
  kpiCard: {
    position: "relative",
    padding: theme.spacing(2),
    borderRadius: 18,
    overflow: "hidden",
    height: "100%",
    background: isDark
      ? "linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.92) 100%)"
      : "#ffffff",
    border: isDark
      ? "1px solid rgba(45, 212, 191, 0.16)"
      : "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: isDark
      ? "0 14px 34px rgba(2, 6, 23, 0.30)"
      : "0 8px 20px -12px rgba(15, 23, 42, 0.25)",
    transition: "transform 0.16s ease, box-shadow 0.16s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: isDark
        ? "0 20px 40px rgba(2, 6, 23, 0.38)"
        : "0 16px 30px -14px rgba(15, 23, 42, 0.30)"
    }
  },
  kpiAccent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4
  },
  kpiTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.75)
  },
  kpiLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    paddingTop: 3
  },
  kpiIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  kpiValue: {
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.05,
    color: theme.palette.text.primary
  },
  kpiDesc: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.35,
    fontWeight: 500
  },

  /* ===== Donut center overlay ===== */
  donutWrap: {
    position: "relative"
  },
  donutCenter: {
    position: "absolute",
    top: "calc(50% - 22px)",
    left: 0,
    right: 0,
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    pointerEvents: "none"
  },
  donutCenterBig: {
    fontSize: "1.6rem",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
    lineHeight: 1
  },
  donutCenterCap: {
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    color: theme.palette.text.disabled,
    marginTop: 2
  },

  /* ===== Horizontal bars ===== */
  barList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.6),
    marginTop: theme.spacing(2)
  },
  barLab: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: 5
  },
  barLabValue: {
    fontWeight: 800,
    color: theme.palette.text.primary
  },
  barTrack: {
    height: 9,
    borderRadius: 999,
    backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "#eef2f6",
    overflow: "hidden"
  },
  barFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #14b8a6 0%, #0f766e 100%)"
  },

  /* ===== Top contacts (rich) ===== */
  contactAvatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #36a08e 0%, #1f6f5c 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.82rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  rankBadgeTop: {
    background: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
    color: "#fff"
  },
  contactMain: {
    minWidth: 0,
    flex: "0 0 180px",
    [theme.breakpoints.down("xs")]: {
      flexBasis: 130
    }
  },
  contactVolume: {
    flex: 1,
    height: 8,
    minWidth: 56,
    borderRadius: 999,
    backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "#e7edf3",
    overflow: "hidden"
  },
  contactVolumeFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #5ecfc0 0%, #0f766e 100%)"
  },
  contactCount: {
    flexShrink: 0,
    fontSize: "0.78rem",
    fontWeight: 800,
    color: isDark ? "#99f6e4" : "#0b6b60",
    backgroundColor: isDark ? "rgba(45,212,191,0.14)" : "#e6f6f3",
    borderRadius: 999,
    padding: "4px 11px",
    minWidth: 42,
    textAlign: "center"
  },
  topContactsItemRich: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 14,
    border: isDark
      ? "1px solid rgba(45,212,191,0.10)"
      : "1px solid rgba(148,163,184,0.16)",
    backgroundColor: isDark ? "rgba(45, 212, 191, 0.06)" : "rgba(248, 250, 252, 0.9)",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      borderColor: isDark ? "rgba(45,212,191,0.30)" : "rgba(20,184,166,0.5)",
      backgroundColor: isDark ? "rgba(45, 212, 191, 0.10)" : "#f1faf7"
    }
  }
  });
});

const KPI_TONES = {
  teal:   { ic: "#e6f6f3", icc: "#0f766e", ac: "#14b8a6", vc: "#0e7c70" },
  amber:  { ic: "#fff8eb", icc: "#c98a05", ac: "#f5b942", vc: null },
  violet: { ic: "#eef0fb", icc: "#7c5cff", ac: "#7c5cff", vc: null },
  green:  { ic: "#e7f6ee", icc: "#1a7a4d", ac: "#30a46c", vc: null },
  blue:   { ic: "#e8f1ff", icc: "#3b82f6", ac: "#3b82f6", vc: null },
  rose:   { ic: "#feecec", icc: "#c4292e", ac: "#e5484d", vc: "#c4292e" }
};

const KPI_TONES_DARK = {
  teal:   { ic: "rgba(20,184,166,0.16)", icc: "#5eead4", ac: "#14b8a6", vc: "#5eead4" },
  amber:  { ic: "rgba(245,158,11,0.18)", icc: "#fbbf24", ac: "#f59e0b", vc: null },
  violet: { ic: "rgba(124,92,255,0.20)", icc: "#a78bfa", ac: "#7c5cff", vc: null },
  green:  { ic: "rgba(48,164,108,0.18)", icc: "#4ade80", ac: "#30a46c", vc: null },
  blue:   { ic: "rgba(59,130,246,0.18)", icc: "#60a5fa", ac: "#3b82f6", vc: null },
  rose:   { ic: "rgba(229,72,77,0.20)", icc: "#fca5a5", ac: "#e5484d", vc: "#fca5a5" }
};

const getKpiTone = (tone, isDark) =>
  (isDark ? KPI_TONES_DARK : KPI_TONES)[tone] || (isDark ? KPI_TONES_DARK : KPI_TONES).teal;

const getInitials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// HH:MM:SS, como no resumo de referencia. Horas nao estouram em dias: um
// chamado parado ha 30h le melhor como "30:00:00" do que como "1d 06:00:00"
// num cartao que esta ao lado de outros tempos no mesmo formato.
const formatDurationHHMMSS = milliseconds => {
	const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	return [hours, minutes, seconds]
		.map(part => String(part).padStart(2, "0"))
		.join(":");
};

const defaultSummary = {
  metrics: {
    usersCount: 0,
    queuesCount: 0,
    connectionsCount: 0,
    contactsCount: 0,
    openTicketsCount: 0,
    pendingTicketsCount: 0,
    closedTicketsTodayCount: 0,
    unreadTicketsCount: 0,
    groupTicketsCount: 0,
    privateTicketsCount: 0,
    internalTicketsCount: 0,
    internalTicketsDueSoonCount: 0,
    internalTicketsOverdueCount: 0,
    // null = fora de escopo (dashboard restrita a um atendente). Zero seria
    // um numero legitimo e a tela nao saberia diferenciar de "sem dados".
    usersOnlineCount: null,
    usersAwayCount: null,
    usersOfflineCount: null,
    usersWithOpenTicketsCount: null,
    usersWithoutOpenTicketsCount: null,
    openTicketsAverageAgeMs: null
  },
  scope: {
    kind: "company",
    userId: null
  },
  queuePressure: [],
  tagBreakdown: { items: [], taggedTickets: 0, untaggedTickets: 0, totalTickets: 0 },
  reasonsByQueue: [],
  reasonsByCategory: [],
  businessHoursByState: {
    open: { businessHours: 0, afterHours: 0, saturday: 0, sunday: 0 },
    pending: { businessHours: 0, afterHours: 0, saturday: 0, sunday: 0 }
  },
  agentRecognition: [],
  businessHoursBreakdown: {
    businessHours: 0,
    afterHours: 0,
    saturday: 0,
    sunday: 0,
    total: 0
  },
  workloadDistributions: {
    byUser: [],
    byQueue: [],
    byWhatsapp: [],
    byHandledMode: []
  },
  topContacts: [],
  topContactsPeriodStart: null,
  analyticsPeriod: {
    key: "today",
    label: "Hoje",
    startDate: null,
    endDate: null,
    timeZone: "America/Sao_Paulo",
    dateBasis: "closedAt",
    dateBasisLabel: "Data de fechamento"
  },
  availableUsers: [],
  availableQueues: [],
  attendantTimeline: {
    queueId: null,
    bucket: "hour",
    points: [],
    series: []
  },
  performance: {
    filters: {
      userId: null,
      queueId: null
    },
    averages: {
      tmeMs: 0,
      tmaMs: 0,
      tmeMinutes: 0,
      tmaMinutes: 0,
      tmeCount: 0,
      tmaCount: 0
    },
    trend: {
      bucket: "hour",
      points: []
    }
  },
  ratings: {
    enabled: false,
    average: 0,
    totalAnswered: 0,
    pendingResponses: 0,
    distribution: [],
    byMode: [],
    latest: []
  },
  commercialAlert: null
};

const escapeHtml = value =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getHandledModeLabel = (value, texts) =>
  texts.panels.ratingsMode?.[value] ||
  texts.panels.ratingsMode?.unknown ||
  value;

const mapHandledModeDistribution = (rows = [], texts) =>
  rows.map(item => ({
    ...item,
    label: getHandledModeLabel(item.label, texts)
  }));

const buildExcelTable = (title, rows) => {
  const body = rows
    .map(
      row =>
        `<tr>${row
          .map(cell => `<td>${escapeHtml(cell)}</td>`)
          .join("")}</tr>`
    )
    .join("");

  return `
    <table border="1" cellspacing="0" cellpadding="6">
      <tr><th colspan="${rows[0]?.length || 1}">${escapeHtml(title)}</th></tr>
      ${body}
    </table>
    <br />
  `;
};

const exportDashboardToExcel = (
  summary,
  companyName,
  selectedQueueName,
  selectedDateBasisLabel,
  selectedPeriodLabel,
  texts
) => {
  const metricRows = [
    ["Indicador", "Valor"],
    [texts.export.metrics.open, summary.metrics.openTicketsCount],
    [texts.export.metrics.pending, summary.metrics.pendingTicketsCount],
    [texts.export.metrics.unread, summary.metrics.unreadTicketsCount],
    [texts.export.metrics.closedToday, summary.metrics.closedTicketsTodayCount],
    [texts.export.metrics.tme, Number(summary?.performance?.averages?.tmeMinutes || 0).toFixed(2)],
    [texts.export.metrics.tma, Number(summary?.performance?.averages?.tmaMinutes || 0).toFixed(2)],
    [texts.export.metrics.users, summary.metrics.usersCount],
    [texts.export.metrics.queues, summary.metrics.queuesCount],
    [texts.export.metrics.connections, summary.metrics.connectionsCount],
    [texts.export.metrics.contacts, summary.metrics.contactsCount],
    ["Conversas privadas", summary.metrics.privateTicketsCount],
    ["Grupos", summary.metrics.groupTicketsCount],
    ["Tickets internos", summary.metrics.internalTicketsCount],
    ["Internos a vencer", summary.metrics.internalTicketsDueSoonCount],
    ["Internos vencidos", summary.metrics.internalTicketsOverdueCount],
    ["Usuários online", summary.metrics.usersOnlineCount ?? "-"],
    ["Usuários ausentes", summary.metrics.usersAwayCount ?? "-"],
    ["Usuários offline", summary.metrics.usersOfflineCount ?? "-"],
    ["Usuários com atendimento", summary.metrics.usersWithOpenTicketsCount ?? "-"],
    ["Usuários sem atendimento", summary.metrics.usersWithoutOpenTicketsCount ?? "-"],
    [
      "Média dos chamados abertos",
      summary.metrics.openTicketsAverageAgeMs === null
        ? "-"
        : formatDurationHHMMSS(summary.metrics.openTicketsAverageAgeMs)
    ]
  ];

  const byUserRows = [
    [texts.export.attendant, texts.export.ticketsInPeriod],
    ...(summary.workloadDistributions.byUser || []).map(item => [item.label, item.value])
  ];
  const byQueueRows = [
    [texts.export.queue, texts.export.ticketsInPeriod],
    ...(summary.workloadDistributions.byQueue || []).map(item => [item.label, item.value])
  ];
  const byWhatsappRows = [
    [texts.export.connection, texts.export.ticketsInPeriod],
    ...(summary.workloadDistributions.byWhatsapp || []).map(item => [item.label, item.value])
  ];
  const byHandledModeRows = [
    [texts.export.handledMode, texts.export.ticketsInPeriod],
    ...mapHandledModeDistribution(
      summary.workloadDistributions.byHandledMode || [],
      texts
    ).map(item => [item.label, item.value])
  ];
  const topContactsRows = [
    [texts.export.client, texts.export.number, texts.export.volume],
    ...(summary.topContacts || []).map(item => [
      item.label,
      item.secondary || "-",
      item.value
    ])
  ];
  const timelineRows = [
    ["Período", ...(summary.attendantTimeline.series || [])],
    ...(summary.attendantTimeline.points || []).map(point => [
      point.label,
      ...(summary.attendantTimeline.series || []).map(series => point[series] || 0)
    ])
  ];

  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8" /></head>
      <body>
        <h2>${escapeHtml(companyName)} - ${escapeHtml(texts.title)}</h2>
        <p>${escapeHtml(texts.export.period)}: ${escapeHtml(selectedPeriodLabel || texts.periods.today)}</p>
        <p>${escapeHtml(texts.export.dateBasis)}: ${escapeHtml(selectedDateBasisLabel || texts.dateBasis.closedAt)}</p>
        <p>${escapeHtml(texts.export.chartQueue)}: ${escapeHtml(selectedQueueName || texts.all)}</p>
        <p>Gerado em ${escapeHtml(new Date().toLocaleString("pt-BR"))}</p>
        ${buildExcelTable(texts.export.currentView, metricRows)}
        ${buildExcelTable(texts.export.byAttendant, byUserRows)}
        ${buildExcelTable(texts.export.byQueue, byQueueRows)}
        ${buildExcelTable(texts.export.byConnection, byWhatsappRows)}
        ${buildExcelTable(texts.export.byHandledMode, byHandledModeRows)}
        ${buildExcelTable(texts.export.topClients, topContactsRows)}
        ${buildExcelTable(texts.export.attendantRhythm, timelineRows)}
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff", html], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `dashboard-gerencial-${new Date()
    .toISOString()
    .slice(0, 10)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const KpiCard = ({ classes, tone = "teal", title, value, subtitle, icon }) => {
  const theme = useTheme();
  const T = getKpiTone(tone, theme.palette.type === "dark");

  return (
    <Paper elevation={0} className={classes.kpiCard}>
      <span className={classes.kpiAccent} style={{ backgroundColor: T.ac }} />
      <div className={classes.kpiTop}>
        <Typography className={classes.kpiLabel}>{title}</Typography>
        <span
          className={classes.kpiIcon}
          style={{ backgroundColor: T.ic, color: T.icc }}
        >
          {icon}
        </span>
      </div>
      <Typography
        variant="h4"
        className={classes.kpiValue}
        style={T.vc ? { color: T.vc } : undefined}
      >
        {value}
      </Typography>
      <Typography variant="body2" className={classes.kpiDesc}>
        {subtitle}
      </Typography>
    </Paper>
  );
};

// Faixas exclusivas: os quatro numeros somam o total do periodo. Por isso
// "fora do expediente" diz explicitamente "seg a sex" - sem isso o usuario
// esperaria que sabado e domingo estivessem dentro dele.
const BusinessHoursPanel = ({
  classes,
  texts,
  breakdown,
  byState,
  periodLabel,
  dateBasisLabel
}) => {
  const data = breakdown || {};
  const total = Number(data.total || 0);

  const cards = [
    { key: "businessHours", tone: "teal", label: texts.businessHours.inHours },
    { key: "afterHours", tone: "amber", label: texts.businessHours.afterHours },
    { key: "saturday", tone: "violet", label: texts.businessHours.saturday },
    { key: "sunday", tone: "rose", label: texts.businessHours.sunday }
  ];

  const share = value =>
    total > 0 ? `${Math.round((Number(value || 0) / total) * 100)}%` : "0%";

  return (
    <Paper elevation={0} className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>
            {texts.businessHours.title}
          </Typography>
          <Typography variant="body2" className={classes.panelSubtitle}>
            {interpolate(texts.businessHours.subtitle, {
              period: String(periodLabel || "").toLowerCase(),
              basis: String(dateBasisLabel || "").toLowerCase(),
              total
            })}
          </Typography>
        </div>
      </div>
      <Grid container spacing={2}>
        {cards.map(card => (
          <Grid item xs={12} sm={6} md={3} key={card.key}>
            <KpiCard
              classes={classes}
              tone={card.tone}
              title={card.label}
              value={Number(data[card.key] || 0)}
              subtitle={interpolate(texts.businessHours.share, {
                share: share(data[card.key])
              })}
              icon={<AccessTimeIcon />}
            />
          </Grid>
        ))}
      </Grid>
      {byState ? (
        <div className={classes.barList}>
          <Typography variant="body2" className={classes.panelSubtitle}>
            {texts.businessHours.byStateTitle}
          </Typography>
          {[
            { key: "open", label: texts.businessHours.stateOpen },
            { key: "pending", label: texts.businessHours.statePending }
          ].map(state => {
            const row = byState[state.key] || {};
            const rowTotal =
              Number(row.businessHours || 0) +
              Number(row.afterHours || 0) +
              Number(row.saturday || 0) +
              Number(row.sunday || 0);

            return (
              <div key={`state-${state.key}`} className={classes.barLab}>
                <span>
                  <strong>{state.label}</strong> — {rowTotal}
                </span>
                <span className={classes.barLabValue}>
                  {interpolate(texts.businessHours.stateDetail, {
                    inHours: Number(row.businessHours || 0),
                    afterHours: Number(row.afterHours || 0),
                    saturday: Number(row.saturday || 0),
                    sunday: Number(row.sunday || 0)
                  })}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </Paper>
  );
};

const PiePanel = ({ classes, title, subtitle, data }) => {
  const theme = useTheme();
  const texts = getDashboardTexts();
  const total = (data || []).reduce((sum, item) => sum + Number(item.value || 0), 0);

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>{title}</Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {subtitle}
      </Typography>
      {total > 0 ? (
        <div className={classes.donutWrap}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={62}
                outerRadius={94}
                paddingAngle={3}
              >
                {data.map((item, index) => (
                  <Cell
                    key={`${title}-${item.label}`}
                    fill={chartPalette[index % chartPalette.length]}
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                formatter={value => (
                  <span style={{ color: theme.palette.text.secondary }}>{value}</span>
                )}
              />
              <RechartsTooltip
                formatter={value => [
                  `${value} ${texts.panels.attendanceSuffix}`,
                  texts.panels.volumeTooltip
                ]}
                labelFormatter={label => label}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className={classes.donutCenter}>
            <span className={classes.donutCenterBig}>{total}</span>
            <span className={classes.donutCenterCap}>TOTAL</span>
          </div>
        </div>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.panels.pieEmpty}
        </Typography>
      )}
    </Paper>
  );
};

const BarPanel = ({ classes, title, subtitle, data }) => {
  const texts = getDashboardTexts();
  const items = (data || []).filter(item => Number(item.value || 0) > 0);
  const maxValue = items.reduce(
    (max, item) => Math.max(max, Number(item.value || 0)),
    0
  );

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>{title}</Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {subtitle}
      </Typography>
      {items.length > 0 ? (
        <div className={classes.barList}>
          {items.slice(0, 6).map(item => (
            <div key={`${title}-${item.label}`}>
              <div className={classes.barLab}>
                <span>{item.label}</span>
                <span className={classes.barLabValue}>{item.value}</span>
              </div>
              <div className={classes.barTrack}>
                <div
                  className={classes.barFill}
                  style={{
                    width: `${maxValue ? (Number(item.value) / maxValue) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.panels.pieEmpty}
        </Typography>
      )}
    </Paper>
  );
};

// Backlog do PRESENTE. O subtitulo diz isso porque todos os outros paineis da
// tela sao do periodo selecionado - sem o aviso, o numero parece histórico.
const QueuePressurePanel = ({ classes, texts, queues, tags }) => {
  const ranked = Array.isArray(queues) ? queues : [];
  const top = ranked[0];
  const tagData = tags || {};
  const tagItems = Array.isArray(tagData.items) ? tagData.items : [];
  const maxTag = tagItems.reduce((max, item) => Math.max(max, Number(item.value || 0)), 0);

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>
        {texts.queuePressure.title}
      </Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {texts.queuePressure.subtitle}
      </Typography>
      {top ? (
        <div className={classes.barList}>
          {ranked.slice(0, 5).map(queue => (
            <div key={`pressure-${queue.queueId || "none"}`}>
              <div className={classes.barLab}>
                <span>{queue.label}</span>
                <span className={classes.barLabValue}>
                  {interpolate(texts.queuePressure.backlogDetail, {
                    backlog: queue.backlog,
                    open: queue.open,
                    pending: queue.pending
                  })}
                </span>
              </div>
              <div className={classes.barTrack}>
                <div
                  className={classes.barFill}
                  style={{
                    width: `${top.backlog ? (queue.backlog / top.backlog) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.queuePressure.empty}
        </Typography>
      )}
      <Typography className={classes.panelTitle} style={{ marginTop: 24 }}>
        {texts.queuePressure.tagsTitle}
      </Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {interpolate(texts.queuePressure.tagsSubtitle, {
          tagged: Number(tagData.taggedTickets || 0),
          untagged: Number(tagData.untaggedTickets || 0),
          total: Number(tagData.totalTickets || 0)
        })}
      </Typography>
      {tagItems.length > 0 ? (
        <div className={classes.barList}>
          {tagItems.slice(0, 8).map(tag => (
            <div key={`tag-${tag.id}`}>
              <div className={classes.barLab}>
                <span>{tag.label}</span>
                <span className={classes.barLabValue}>{tag.value}</span>
              </div>
              <div className={classes.barTrack}>
                <div
                  className={classes.barFill}
                  style={{
                    width: `${maxTag ? (Number(tag.value) / maxTag) * 100 : 0}%`,
                    backgroundColor: tag.color || undefined
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.queuePressure.tagsEmpty}
        </Typography>
      )}
    </Paper>
  );
};

// Mesmo desenho do painel por fila. A categoria sai do prefixo do proprio
// assunto ("Comercial - Sem resposta de cliente"), entao nao ha cadastro novo.
const ReasonsByCategoryPanel = ({ classes, texts, data, periodLabel }) => {
  const categories = Array.isArray(data) ? data : [];

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>
        {texts.reasonCategories.title}
      </Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {interpolate(texts.reasonCategories.subtitle, {
          period: String(periodLabel || "").toLowerCase()
        })}
      </Typography>
      {categories.length > 0 ? (
        <Grid container spacing={2}>
          {categories.slice(0, 6).map(category => {
            const maxReason = (category.reasons || []).reduce(
              (max, item) => Math.max(max, Number(item.value || 0)),
              0
            );

            return (
              <Grid item xs={12} md={4} key={`cat-${category.label}`}>
                <div className={classes.barLab}>
                  <strong>{category.label}</strong>
                  <span className={classes.barLabValue}>{category.total}</span>
                </div>
                <div className={classes.barList}>
                  {(category.reasons || []).map(reason => (
                    <div key={`${category.label}-${reason.label}`}>
                      <div className={classes.barLab}>
                        <span>{reason.label}</span>
                        <span className={classes.barLabValue}>
                          {reason.value}
                          {category.total
                            ? ` (${Math.round((reason.value / category.total) * 100)}%)`
                            : ""}
                        </span>
                      </div>
                      <div className={classes.barTrack}>
                        <div
                          className={classes.barFill}
                          style={{
                            width: `${maxReason ? (reason.value / maxReason) * 100 : 0}%`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.reasonCategories.empty}
        </Typography>
      )}
    </Paper>
  );
};

const ReasonsByQueuePanel = ({ classes, texts, data, periodLabel }) => {
  const queues = Array.isArray(data) ? data : [];

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>{texts.reasons.title}</Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {interpolate(texts.reasons.subtitle, {
          period: String(periodLabel || "").toLowerCase()
        })}
      </Typography>
      {queues.length > 0 ? (
        <Grid container spacing={2}>
          {queues.slice(0, 6).map(queue => {
            const maxReason = (queue.reasons || []).reduce(
              (max, item) => Math.max(max, Number(item.value || 0)),
              0
            );

            return (
              <Grid item xs={12} md={4} key={`reasons-${queue.queueId || "none"}`}>
                <div className={classes.barLab}>
                  <strong>{queue.label}</strong>
                  <span className={classes.barLabValue}>{queue.total}</span>
                </div>
                <div className={classes.barList}>
                  {(queue.reasons || []).map(reason => (
                    <div key={`${queue.queueId}-${reason.label}`}>
                      <div className={classes.barLab}>
                        <span>{reason.label}</span>
                        <span className={classes.barLabValue}>
                          {reason.value}
                          {queue.total
                            ? ` (${Math.round((reason.value / queue.total) * 100)}%)`
                            : ""}
                        </span>
                      </div>
                      <div className={classes.barTrack}>
                        <div
                          className={classes.barFill}
                          style={{
                            width: `${maxReason ? (reason.value / maxReason) * 100 : 0}%`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.reasons.empty}
        </Typography>
      )}
    </Paper>
  );
};

const AgentRecognitionPanel = ({ classes, texts, data, periodLabel }) => {
  const agents = Array.isArray(data) ? data : [];
  // Melhor CSAT pelo score ajustado, nao pela media crua - senao uma unica nota
  // 5 lidera. A media crua e a contagem aparecem na linha para dar transparencia.
  const bestCsat = agents
    .filter(agent => agent.ratingScore !== null && agent.ratingScore !== undefined)
    .sort((a, b) => b.ratingScore - a.ratingScore)[0];

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.panelTitle}>
        {texts.recognition.title}
      </Typography>
      <Typography variant="body2" className={classes.panelSubtitle}>
        {interpolate(texts.recognition.subtitle, {
          period: String(periodLabel || "").toLowerCase()
        })}
      </Typography>
      {agents.length > 0 ? (
        <>
          {bestCsat ? (
            <Typography variant="body2" className={classes.panelSubtitle}>
              {interpolate(texts.recognition.bestCsat, {
                name: bestCsat.label,
                score: bestCsat.ratingScore,
                average: bestCsat.ratingAverage,
                count: bestCsat.ratingCount
              })}
            </Typography>
          ) : null}
          <div className={classes.barList}>
            {agents.slice(0, 8).map(agent => (
              <div key={`agent-${agent.userId}`}>
                <div className={classes.barLab}>
                  <span>
                    <strong>{agent.label}</strong>
                    {agent.topQueue ? ` — ${agent.topQueue.label}` : ""}
                  </span>
                  <span className={classes.barLabValue}>
                    {interpolate(texts.recognition.agentValue, {
                      total: agent.total,
                      csat:
                        agent.ratingAverage === null
                          ? texts.recognition.noRating
                          : `${agent.ratingAverage} (${agent.ratingCount})`
                    })}
                  </span>
                </div>
                <div className={classes.barLab}>
                  <span className={classes.barLabValue}>
                    {(agent.topReasons || []).map(item => item.label).join(" · ") ||
                      texts.recognition.noReasons}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.recognition.empty}
        </Typography>
      )}
    </Paper>
  );
};

const AttendantTimelinePanel = ({
  classes,
  timeline,
  availableQueues,
  selectedQueueId,
  onQueueChange,
  periodLabel,
  timeZone
}) => {
  const theme = useTheme();
  const texts = getDashboardTexts();
  const points = Array.isArray(timeline?.points) ? timeline.points : [];
  const series = Array.isArray(timeline?.series) ? timeline.series : [];

  return (
    <Paper className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>{texts.panels.timelineTitle}</Typography>
          <Typography variant="body2" className={classes.panelSubtitle}>
            {interpolate(texts.panels.timelineSubtitle, { period: periodLabel.toLowerCase() })}
          </Typography>
        </div>
        <TextField
          select
          size="small"
          variant="outlined"
          label={texts.panels.queue}
          value={selectedQueueId}
          onChange={event => onQueueChange(event.target.value)}
          className={classes.lineFilters}
        >
          <MenuItem value="">{texts.all}</MenuItem>
          {availableQueues.map(queue => (
            <MenuItem key={queue.id} value={String(queue.id)}>
              {queue.name}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {series.length > 0 ? (
        <ResponsiveContainer width="100%" height={340}>
          <LineChart
            data={points}
            margin={{ top: 12, right: 24, left: 0, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.30)" : "rgba(148, 163, 184, 0.22)"} />
            <XAxis dataKey="label" stroke={theme.palette.text.secondary} />
            <YAxis allowDecimals={false} stroke={theme.palette.text.secondary} />
            <RechartsTooltip />
            <Legend />
            {series.map((seriesName, index) => (
              <Line
                key={seriesName}
                type={timeline?.bucket === "hour" ? "linear" : "monotone"}
                dataKey={seriesName}
                stroke={chartPalette[index % chartPalette.length]}
                strokeWidth={timeline?.bucket === "hour" ? 3 : 2.5}
                dot={{ r: timeline?.bucket === "hour" ? 4 : 2 }}
                activeDot={{ r: timeline?.bucket === "hour" ? 6 : 5 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.panels.timelineEmpty}
        </Typography>
      )}
    </Paper>
  );
};

const PerformancePanel = ({
  classes,
  performance,
  availableUsers,
  availableQueues,
  selectedUserId,
  selectedQueueId,
  onUserChange,
  onQueueChange,
  periodLabel
}) => {
  const theme = useTheme();
  const texts = getDashboardTexts();
  const trendPoints = Array.isArray(performance?.trend?.points) ? performance.trend.points : [];
  const averages = performance?.averages || {};
  const hasData = trendPoints.some(
    point => Number(point?.tmeCount || 0) > 0 || Number(point?.tmaCount || 0) > 0
  );

  return (
    <Paper className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>{texts.panels.performanceTitle}</Typography>
          <Typography variant="body2" className={classes.panelSubtitle}>
            {interpolate(texts.panels.performanceSubtitle, { period: periodLabel.toLowerCase() })}
          </Typography>
        </div>
        <div className={classes.performanceFilters}>
          <TextField
            select
            size="small"
            variant="outlined"
            label={texts.panels.attendant}
            value={selectedUserId}
            onChange={event => onUserChange(event.target.value)}
            className={classes.lineFilters}
          >
            <MenuItem value="">{texts.allMale}</MenuItem>
            {(availableUsers || []).map(user => (
              <MenuItem key={user.id} value={String(user.id)}>
                {user.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            variant="outlined"
            label={texts.panels.queue}
            value={selectedQueueId}
            onChange={event => onQueueChange(event.target.value)}
            className={classes.lineFilters}
          >
            <MenuItem value="">{texts.all}</MenuItem>
            {(availableQueues || []).map(queue => (
              <MenuItem key={queue.id} value={String(queue.id)}>
                {queue.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <div className={classes.performanceStatsGrid}>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.averageTme}</div>
          <div className={classes.statValue}>
            {Number(averages.tmeMinutes || 0).toFixed(2)} min
          </div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.averageTma}</div>
          <div className={classes.statValue}>
            {Number(averages.tmaMinutes || 0).toFixed(2)} min
          </div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.firstResponse}</div>
          <div className={classes.statValue}>{Number(averages.tmeCount || 0)}</div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.completeTma}</div>
          <div className={classes.statValue}>{Number(averages.tmaCount || 0)}</div>
        </div>
      </div>

      {hasData ? (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={trendPoints}
            margin={{ top: 12, right: 24, left: 0, bottom: 8 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={
                theme.palette.type === "dark"
                  ? "rgba(148, 163, 184, 0.30)"
                  : "rgba(148, 163, 184, 0.22)"
              }
            />
            <XAxis dataKey="label" stroke={theme.palette.text.secondary} />
            <YAxis stroke={theme.palette.text.secondary} />
            <RechartsTooltip
              formatter={(value, name) => [
                `${Number(value || 0).toFixed(2)} min`,
                name === "tmeMinutes" ? "TME" : "TMA"
              ]}
            />
            <Legend
              formatter={value => (
                <span style={{ color: theme.palette.text.secondary }}>
                  {value === "tmeMinutes" ? "TME (min)" : "TMA (min)"}
                </span>
              )}
            />
            <Line
              type={performance?.trend?.bucket === "hour" ? "linear" : "monotone"}
              dataKey="tmeMinutes"
              stroke="#0f766e"
              strokeWidth={3}
              dot={{ r: 3.5 }}
              activeDot={{ r: 6 }}
              connectNulls
            />
            <Line
              type={performance?.trend?.bucket === "hour" ? "linear" : "monotone"}
              dataKey="tmaMinutes"
              stroke="#f59e0b"
              strokeWidth={3}
              dot={{ r: 3.5 }}
              activeDot={{ r: 6 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.panels.performanceEmpty}
        </Typography>
      )}
    </Paper>
  );
};

const TopContactsPanel = ({ classes, data, periodLabel }) => {
  const texts = getDashboardTexts();

  return (
  <Paper className={classes.panel}>
    <Typography className={classes.panelTitle}>{texts.panels.topClientsTitle}</Typography>
    <Typography variant="body2" className={classes.panelSubtitle}>
      {interpolate(texts.panels.topClientsSubtitle, { period: periodLabel.toLowerCase() })}
    </Typography>
    {data.length > 0 ? (
      <div className={classes.topContactsList} style={{ marginTop: 16 }}>
        {data.map((item, index) => {
          const maxValue = data.reduce(
            (max, entry) => Math.max(max, Number(entry.value || 0)),
            0
          );
          return (
            <div
              className={classes.topContactsItemRich}
              key={`top-contact-${item.id || item.label}-${index}`}
            >
              <div
                className={`${classes.rankBadge} ${
                  index < 3 ? classes.rankBadgeTop : ""
                }`}
              >
                {index + 1}
              </div>
              <div className={classes.contactAvatar}>{getInitials(item.label)}</div>
              <div className={classes.contactMain}>
                <div className={classes.topContactsName}>{item.label}</div>
                <div className={classes.topContactsMeta}>
                  {item.secondary || texts.panels.noNumber}
                </div>
              </div>
              <div className={classes.contactVolume}>
                <div
                  className={classes.contactVolumeFill}
                  style={{
                    width: `${maxValue ? (Number(item.value) / maxValue) * 100 : 0}%`
                  }}
                />
              </div>
              <div className={classes.contactCount}>{item.value}</div>
            </div>
          );
        })}
      </div>
    ) : (
      <Typography variant="body2" className={classes.emptyState}>
        {texts.panels.topClientsEmpty}
      </Typography>
    )}
  </Paper>
  );
};

const RatingsPanel = ({ classes, ratings, periodLabel }) => {
  const texts = getDashboardTexts();
  const distribution = Array.isArray(ratings?.distribution)
    ? ratings.distribution
    : [];
  const latest = Array.isArray(ratings?.latest) ? ratings.latest : [];
  const totalAnswered = Number(ratings?.totalAnswered || 0);
  const maxCount = Math.max(...distribution.map(item => Number(item.count || 0)), 1);
  const modeLabel = value =>
    texts.panels.ratingsMode?.[value] || texts.panels.ratingsMode?.unknown || value;

  return (
    <Paper className={classes.panel}>
      <div className={classes.panelHeader}>
        <div>
          <Typography className={classes.panelTitle}>{texts.panels.ratingsTitle}</Typography>
          <Typography variant="body2" className={classes.panelSubtitle}>
            {interpolate(texts.panels.ratingsSubtitle, { period: periodLabel.toLowerCase() })}
          </Typography>
        </div>
      </div>

      <div className={classes.ratingHeaderStats}>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.ratingsAverage}</div>
          <div className={classes.ratingScore}>
            <span className={classes.ratingScoreValue}>
              {Number(ratings?.average || 0).toFixed(2)}
            </span>
            <span>/ 5</span>
          </div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.ratingsAnswered}</div>
          <div className={classes.statValue}>{totalAnswered}</div>
        </div>
        <div className={classes.statItem}>
          <div className={classes.statLabel}>{texts.panels.ratingsPending}</div>
          <div className={classes.statValue}>{Number(ratings?.pendingResponses || 0)}</div>
        </div>
      </div>

      {totalAnswered > 0 ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography className={classes.panelTitle}>
              {texts.panels.ratingsDistribution}
            </Typography>
            <div className={classes.ratingDistribution}>
              {distribution.map(item => {
                const count = Number(item.count || 0);
                const width = Math.max(3, Math.round((count / maxCount) * 100));
                return (
                  <div className={classes.ratingDistributionRow} key={`rating-${item.score}`}>
                    <span>{item.score}/5</span>
                    <div className={classes.ratingBarTrack}>
                      <div
                        className={classes.ratingBarFill}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <strong>{count}</strong>
                  </div>
                );
              })}
            </div>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography className={classes.panelTitle}>
              {texts.panels.ratingsLatest}
            </Typography>
            <div className={classes.ratingLatestList}>
              {latest.map(item => (
                <div className={classes.ratingLatestItem} key={`latest-rating-${item.id}`}>
                  <div className={classes.ratingScore}>
                    <span className={classes.ratingScoreValue}>
                      {Number(item.ratingValue || 0)}
                    </span>
                    <span>/ 5</span>
                  </div>
                  <Typography variant="body2">
                    {item.comment || texts.panels.ratingsNoComment}
                  </Typography>
                  <div className={classes.ratingLatestMeta}>
                    <span>{item.contact?.name || texts.panels.noNumber}</span>
                    <span>{item.queue?.name || texts.panels.queue}</span>
                    <span>{modeLabel(item.handledByMode || "unknown")}</span>
                  </div>
                </div>
              ))}
            </div>
          </Grid>
        </Grid>
      ) : (
        <Typography variant="body2" className={classes.emptyState}>
          {texts.panels.ratingsNoData}
        </Typography>
      )}
    </Paper>
  );
};

const Dashboard = () => {
  const classes = useStyles();
  const texts = getDashboardTexts();
  const { user } = useContext(AuthContext);
  const companyName = user?.company?.name || "DeskAdvisor";
  const isUserScoped = user?.profile === "user";
  const [summary, setSummary] = useState(defaultSummary);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("today");
  // Nasce com o mes corrente ja preenchido: um seletor personalizado vazio
  // obriga o usuario a preencher duas datas antes de ver qualquer coisa.
  const [customDateFrom, setCustomDateFrom] = useState(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [customDateTo, setCustomDateTo] = useState(() => toDateInputValue(new Date()));
  const [selectedDateBasis, setSelectedDateBasis] = useState("closedAt");
  const [selectedTimelineQueueId, setSelectedTimelineQueueId] = useState("");
  const [selectedPerformanceUserId, setSelectedPerformanceUserId] = useState("");
  const [selectedPerformanceQueueId, setSelectedPerformanceQueueId] = useState("");
  const latestSummaryRequestId = useRef(0);
  const periodOptions = useMemo(() => getPeriodOptions(texts), [texts]);
  const dateBasisOptions = useMemo(() => getDateBasisOptions(texts), [texts]);

  const fetchSummary = useCallback(async () => {
    const requestId = latestSummaryRequestId.current + 1;
    latestSummaryRequestId.current = requestId;

    try {
      setLoading(true);
      const { data } = await api.get("/dashboard/summary", {
        params: {
          period: selectedPeriod,
          ...(selectedPeriod === "custom"
            ? { dateFrom: customDateFrom, dateTo: customDateTo }
            : {}),
          dateBasis: selectedDateBasis,
          timelineQueueId: selectedTimelineQueueId || undefined,
          performanceUserId: selectedPerformanceUserId || undefined,
          performanceQueueId: selectedPerformanceQueueId || undefined
        }
      });

      if (latestSummaryRequestId.current !== requestId) {
        return;
      }

      setSummary({
        ...defaultSummary,
        ...data,
        workloadDistributions: {
          ...defaultSummary.workloadDistributions,
          ...(data?.workloadDistributions || {})
        },
        attendantTimeline: {
          ...defaultSummary.attendantTimeline,
          ...(data?.attendantTimeline || {})
        },
        performance: {
          ...defaultSummary.performance,
          ...(data?.performance || {}),
          averages: {
            ...defaultSummary.performance.averages,
            ...(data?.performance?.averages || {})
          },
          trend: {
            ...defaultSummary.performance.trend,
            ...(data?.performance?.trend || {})
          }
        }
      });
    } catch (err) {
      if (latestSummaryRequestId.current !== requestId) {
        return;
      }
      toastError(err);
    } finally {
      if (latestSummaryRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    selectedDateBasis,
    selectedPeriod,
    customDateFrom,
    customDateTo,
    selectedTimelineQueueId,
    selectedPerformanceUserId,
    selectedPerformanceQueueId
  ]);

  React.useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const managementHighlights = useMemo(() => {
    const topUser = summary.workloadDistributions.byUser?.[0];
    const topQueue = summary.workloadDistributions.byQueue?.[0];
    const topWhatsapp = summary.workloadDistributions.byWhatsapp?.[0];

    return [
      {
        title: texts.highlights.topUser.title,
        value: topUser?.label || texts.distributionEmpty,
        subtitle: topUser
          ? interpolate(texts.highlights.topUser.subtitle, { count: topUser.value })
          : texts.highlights.topUser.empty
      },
      {
        title: texts.highlights.topQueue.title,
        value: topQueue?.label || texts.distributionEmpty,
        subtitle: topQueue
          ? interpolate(texts.highlights.topQueue.subtitle, { count: topQueue.value })
          : texts.highlights.topQueue.empty
      },
      {
        title: texts.highlights.topConnection.title,
        value: topWhatsapp?.label || texts.distributionEmpty,
        subtitle: topWhatsapp
          ? interpolate(texts.highlights.topConnection.subtitle, { count: topWhatsapp.value })
          : texts.highlights.topConnection.empty
      }
    ];
  }, [summary.workloadDistributions, texts]);

  const selectedQueueName = useMemo(() => {
    if (!selectedTimelineQueueId) {
      return texts.all;
    }

    return (
      summary.availableQueues.find(
        queue => String(queue.id) === String(selectedTimelineQueueId)
      )?.name || texts.all
    );
  }, [selectedTimelineQueueId, summary.availableQueues, texts]);

  const selectedDateBasisLabel = useMemo(
    () =>
      dateBasisOptions.find(option => option.value === selectedDateBasis)?.label ||
      texts.dateBasis.closedAt,
    [dateBasisOptions, selectedDateBasis, texts]
  );

  const selectedPeriodLabel = useMemo(
    () =>
      periodOptions.find(option => option.value === selectedPeriod)?.label ||
      texts.periods.today,
    [periodOptions, selectedPeriod, texts]
  );

  // As datas que o periodo cobre, no fuso do painel: "Este trimestre" no
  // primeiro mes do trimestre e o mesmo intervalo de "Este mes", e sem as
  // datas na tela parecia defeito.
  const periodCoverage = useMemo(() => {
    const period = summary.analyticsPeriod || {};
    if (!period.startDate || !period.endDate) return "";
    const formatDay = value => {
      try {
        return new Intl.DateTimeFormat(texts.periodRange.locale || "pt-BR", {
          timeZone: period.timeZone || "America/Sao_Paulo",
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        }).format(new Date(value));
      } catch (_error) {
        return "";
      }
    };
    const from = formatDay(period.startDate);
    const to = formatDay(period.endDate);
    if (!from || !to) return "";
    return from === to
      ? interpolate(texts.periodRange.single, { date: from })
      : interpolate(texts.periodRange.covering, { from, to });
  }, [summary.analyticsPeriod, texts]);

  const periodDescriptor = useMemo(
    () => texts.periodDescriptors[selectedPeriod] || texts.periodDescriptors.today,
    [selectedPeriod, texts]
  );

  const closedTicketsTitle =
    selectedPeriod === "today"
      ? texts.kpis.closedToday.title
      : texts.kpis.closedPeriod.title;
  const closedTicketsSubtitle =
    selectedPeriod === "today"
      ? texts.kpis.closedToday.subtitle
      : texts.kpis.closedPeriod.subtitle;

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Paper className={classes.heroCard}>
        <div className={classes.heroHeader}>
          <div className={classes.heroIdentity}>
            <Typography className={classes.heroEyebrow}>
              {texts.title}
            </Typography>
            <Typography variant="h4" className={classes.heroTitle}>
              {companyName}
            </Typography>
            <div className={classes.periodFilters}>
              {periodOptions.map(option => (
                <Button
                  key={option.value}
                  variant={selectedPeriod === option.value ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => {
                    setSelectedTimelineQueueId("");
                    setSelectedPeriod(option.value);
                  }}
                  className={classes.periodButton}
                >
                  {option.label}
                </Button>
              ))}
              {selectedPeriod === "custom" && (
                <>
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    label={texts.periodRange.from}
                    value={customDateFrom}
                    onChange={event => setCustomDateFrom(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    className={classes.lineFilters}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    type="date"
                    label={texts.periodRange.to}
                    value={customDateTo}
                    onChange={event => setCustomDateTo(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    className={classes.lineFilters}
                  />
                </>
              )}
              <TextField
                select
                size="small"
                variant="outlined"
                label={texts.base}
                value={selectedDateBasis}
                onChange={event => {
                  setSelectedTimelineQueueId("");
                  setSelectedDateBasis(event.target.value);
                }}
                className={classes.lineFilters}
              >
                {dateBasisOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            {periodCoverage && (
              <Typography className={classes.periodCoverage}>
                {periodCoverage}
              </Typography>
            )}
          </div>
          <div className={classes.heroNoticeSlot}>
            {summary.commercialAlert ? (
              <Paper
                elevation={0}
                className={`${classes.commercialAlert} ${
                  summary.commercialAlert.kind === "danger"
                    ? classes.commercialAlertDanger
                    : ""
                }`}
              >
                <Typography
                  className={`${classes.commercialAlertTitle} ${
                    summary.commercialAlert.kind === "danger"
                      ? classes.commercialAlertTitleDanger
                      : ""
                  }`}
                >
                  {summary.commercialAlert.title}
                </Typography>
                <Typography
                  variant="body2"
                  className={`${classes.commercialAlertText} ${
                    summary.commercialAlert.kind === "danger"
                      ? classes.commercialAlertTextDanger
                      : ""
                  }`}
                >
                  {summary.commercialAlert.description}
                </Typography>
              </Paper>
            ) : null}
          </div>
          <div className={classes.heroActionsPanel}>
            <Typography className={classes.heroActionsLabel}>
              {texts.quickActions}
            </Typography>
            <div className={classes.heroActions}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={fetchSummary}
                disabled={loading}
                className={classes.actionButton}
              >
                {texts.refresh}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={() =>
                  exportDashboardToExcel(
                    summary,
                    companyName,
                    selectedQueueName,
                    selectedDateBasisLabel,
                    selectedPeriodLabel,
                    texts
                  )
                }
                className={classes.actionButton}
              >
                {texts.exportExcel}
              </Button>
            </div>
          </div>
        </div>
        <BusinessHoursPanel
          classes={classes}
          texts={texts}
          breakdown={summary.businessHoursBreakdown}
          byState={summary.businessHoursByState}
          periodLabel={selectedPeriodLabel}
          dateBasisLabel={summary.analyticsPeriod?.dateBasisLabel}
        />
        <Grid container spacing={2} className={classes.spotlightGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              tone="teal"
              title={texts.kpis.open.title}
              value={summary.metrics.openTicketsCount}
              subtitle={texts.kpis.open.subtitle}
              icon={<AssessmentOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              tone="amber"
              title={texts.kpis.pending.title}
              value={summary.metrics.pendingTicketsCount}
              subtitle={texts.kpis.pending.subtitle}
              icon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              tone="violet"
              title={texts.kpis.unread.title}
              value={summary.metrics.unreadTicketsCount}
              subtitle={texts.kpis.unread.subtitle}
              icon={<PeopleOutlineIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              classes={classes}
              tone="green"
              title={closedTicketsTitle}
              value={summary.metrics.closedTicketsTodayCount}
              subtitle={closedTicketsSubtitle}
              icon={<TrendingUpIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              tone="blue"
              title={texts.kpis.internalOpen.title}
              value={summary.metrics.internalTicketsCount}
              subtitle={
                isUserScoped
                  ? texts.kpis.internalOpen.userSubtitle
                  : texts.kpis.internalOpen.adminSubtitle
              }
              icon={<AssignmentOutlinedIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              tone="amber"
              title={texts.kpis.dueSoon.title}
              value={summary.metrics.internalTicketsDueSoonCount}
              subtitle={texts.kpis.dueSoon.subtitle}
              icon={<AccessTimeIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              tone="rose"
              title={texts.kpis.overdue.title}
              value={summary.metrics.internalTicketsOverdueCount}
              subtitle={texts.kpis.overdue.subtitle}
              icon={<ErrorOutlineOutlinedIcon />}
            />
          </Grid>

          {/* Indicadores de equipe: escondidos quando a dashboard esta
              restrita a um atendente, para nao expor presenca e ocupacao dos
              colegas numa tela que oculta o resto dos dados deles. */}
          {!isUserScoped && (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="green"
                  title="Usuários online"
                  value={summary.metrics.usersOnlineCount ?? 0}
                  subtitle="Conectados e ativos agora."
                  icon={<PeopleOutlineIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="amber"
                  title="Usuários ausentes"
                  value={summary.metrics.usersAwayCount ?? 0}
                  subtitle="Conectados, mas sem atividade recente."
                  icon={<AccessTimeIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="violet"
                  title="Usuários offline"
                  value={summary.metrics.usersOfflineCount ?? 0}
                  subtitle="Desconectados ou que nunca acessaram."
                  icon={<PeopleOutlineIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="teal"
                  title="Usuários com atendimento"
                  value={summary.metrics.usersWithOpenTicketsCount ?? 0}
                  subtitle="Com pelo menos um chamado aberto atribuído."
                  icon={<AssignmentOutlinedIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="blue"
                  title="Usuários sem atendimento"
                  value={summary.metrics.usersWithoutOpenTicketsCount ?? 0}
                  subtitle="Sem nenhum chamado aberto no momento."
                  icon={<PeopleOutlineIcon />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  classes={classes}
                  tone="rose"
                  title="Média dos chamados abertos"
                  value={formatDurationHHMMSS(
                    summary.metrics.openTicketsAverageAgeMs
                  )}
                  subtitle="Há quanto tempo, em média, estão abertos."
                  icon={<AccessTimeIcon />}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              classes={classes}
              tone="teal"
              title="TME - Primeiro atendimento"
              value={formatDurationHHMMSS(
                Number(summary?.performance?.totals?.tmeMs || 0)
              )}
              subtitle={`Tempo médio até a primeira resposta (${periodDescriptor}).`}
              icon={<AccessTimeIcon />}
            />
          </Grid>
        </Grid>
        <QueuePressurePanel
          classes={classes}
          texts={texts}
          queues={summary.queuePressure}
          tags={summary.tagBreakdown}
        />
        <ReasonsByCategoryPanel
          classes={classes}
          texts={texts}
          data={summary.reasonsByCategory}
          periodLabel={selectedPeriodLabel}
        />
        <ReasonsByQueuePanel
          classes={classes}
          texts={texts}
          data={summary.reasonsByQueue}
          periodLabel={selectedPeriodLabel}
        />
        <AgentRecognitionPanel
          classes={classes}
          texts={texts}
          data={summary.agentRecognition}
          periodLabel={selectedPeriodLabel}
        />
      </Paper>

      <Grid container spacing={3}>
        {!isUserScoped &&
          managementHighlights.map(item => (
          <Grid item xs={12} md={4} key={item.title}>
            <Paper className={classes.panel}>
              <Typography className={classes.panelTitle}>{item.title}</Typography>
              <Typography variant="h5" className={classes.spotlightValue}>
                {item.value}
              </Typography>
              <Typography variant="body2" className={classes.spotlightSubtitle}>
                {item.subtitle}
              </Typography>
            </Paper>
          </Grid>
          ))}

        <Grid item xs={12}>
          <PerformancePanel
            classes={classes}
            performance={summary.performance}
            availableUsers={summary.availableUsers}
            availableQueues={summary.availableQueues}
            selectedUserId={selectedPerformanceUserId}
            selectedQueueId={selectedPerformanceQueueId}
            onUserChange={setSelectedPerformanceUserId}
            onQueueChange={setSelectedPerformanceQueueId}
            periodLabel={periodDescriptor}
          />
        </Grid>

        {summary.ratings?.enabled ? (
          <Grid item xs={12}>
            <RatingsPanel
              classes={classes}
              ratings={summary.ratings}
              periodLabel={periodDescriptor}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} sm={6} md={3}>
          <BarPanel
            classes={classes}
            title={texts.panels.byAttendantTitle}
            subtitle={interpolate(texts.panels.byAttendantSubtitle, { period: periodDescriptor })}
            data={summary.workloadDistributions.byUser || []}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PiePanel
            classes={classes}
            title={texts.panels.byQueueTitle}
            subtitle={interpolate(texts.panels.byQueueSubtitle, { period: periodDescriptor })}
            data={summary.workloadDistributions.byQueue || []}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PiePanel
            classes={classes}
            title={texts.panels.byConnectionTitle}
            subtitle={interpolate(texts.panels.byConnectionSubtitle, { period: periodDescriptor })}
            data={summary.workloadDistributions.byWhatsapp || []}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <PiePanel
            classes={classes}
            title={texts.panels.byHandledModeTitle}
            subtitle={interpolate(texts.panels.byHandledModeSubtitle, { period: periodDescriptor })}
            data={mapHandledModeDistribution(
              summary.workloadDistributions.byHandledMode || [],
              texts
            )}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <AttendantTimelinePanel
            classes={classes}
            timeline={summary.attendantTimeline}
            availableQueues={summary.availableQueues}
            selectedQueueId={selectedTimelineQueueId}
            onQueueChange={setSelectedTimelineQueueId}
            periodLabel={periodDescriptor}
            timeZone={summary.analyticsPeriod.timeZone || "America/Sao_Paulo"}
          />
        </Grid>
        {!isUserScoped && <Grid item xs={12} md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.panelTitle}>{texts.panels.operationalBaseTitle}</Typography>
            <Typography variant="body2" className={classes.panelSubtitle}>
              {texts.panels.operationalBaseSubtitle}
            </Typography>
            <div className={classes.statList}>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>{texts.panels.activeUsers}</div>
                <div className={classes.statValue}>{summary.metrics.usersCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>{texts.panels.configuredQueues}</div>
                <div className={classes.statValue}>{summary.metrics.queuesCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>{texts.panels.whatsappConnections}</div>
                <div className={classes.statValue}>{summary.metrics.connectionsCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>{texts.panels.registeredContacts}</div>
                <div className={classes.statValue}>{summary.metrics.contactsCount}</div>
              </div>
              <div className={classes.statItem}>
                <div className={classes.statLabel}>{texts.panels.privateGroups}</div>
                <div className={classes.statValue}>
                  {summary.metrics.privateTicketsCount} / {summary.metrics.groupTicketsCount}
                </div>
              </div>
            </div>
          </Paper>
        </Grid>}

        <Grid item xs={12}>
          <TopContactsPanel
            classes={classes}
            data={summary.topContacts || []}
            periodLabel={periodDescriptor}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;


